import { http } from "@/lib/http";

import type {
  AsyncOpResponse,
  BatchAckResponse,
  BatchSendResponse,
  GrantResource,
  Paginated,
  QueueCreateRequest,
  QueueResource,
  QueueUpdateRequest,
  ReceiveResponse,
  SendMessageRequest,
  SendMessageResponse,
} from "./types";

export async function listQueues(params: {
  page: number;
  pageSize: number;
  name?: string;
}): Promise<Paginated<QueueResource>> {
  const { data } = await http.get<Paginated<QueueResource>>("/queues", {
    params: {
      page: params.page,
      page_size: params.pageSize,
      name: params.name || undefined,
    },
  });
  return data;
}

export async function getQueue(id: string): Promise<QueueResource> {
  const { data } = await http.get<QueueResource>(`/queues/${id}`);
  return data;
}

export async function createQueue(
  payload: QueueCreateRequest,
): Promise<QueueResource> {
  const { data } = await http.post<QueueResource>("/queues", payload);
  return data;
}

export async function updateQueue(
  id: string,
  payload: QueueUpdateRequest,
): Promise<QueueResource> {
  const { data } = await http.put<QueueResource>(`/queues/${id}`, payload);
  return data;
}

export async function deleteQueue(id: string): Promise<AsyncOpResponse> {
  const { data } = await http.delete<AsyncOpResponse>(`/queues/${id}`);
  return data;
}

export async function purgeQueue(id: string): Promise<AsyncOpResponse> {
  const { data } = await http.post<AsyncOpResponse>(`/queues/${id}/purge`);
  return data;
}

export async function redriveQueue(
  id: string,
  destinationQueueId: string,
): Promise<AsyncOpResponse> {
  const { data } = await http.post<AsyncOpResponse>(`/queues/${id}/redrive`, {
    destination_queue_id: destinationQueueId,
  });
  return data;
}

export async function sendMessage(
  id: string,
  payload: SendMessageRequest,
): Promise<SendMessageResponse> {
  const { data } = await http.post<SendMessageResponse>(
    `/queues/${id}/messages`,
    payload,
  );
  return data;
}

export async function receiveMessages(
  id: string,
  params: { maxMessages?: number; waitSeconds?: number },
): Promise<ReceiveResponse> {
  const { data } = await http.post<ReceiveResponse>(
    `/queues/${id}/messages/receive`,
    {
      max_messages: params.maxMessages ?? 5,
      wait_seconds: params.waitSeconds ?? 0,
    },
  );
  return data;
}

export async function ackMessage(
  id: string,
  receiptHandle: string,
): Promise<void> {
  await http.post(`/queues/${id}/messages/ack`, {
    receipt_handle: receiptHandle,
  });
}

export async function changeMessageVisibility(
  id: string,
  receiptHandle: string,
  visibilityTimeoutSeconds: number,
): Promise<void> {
  await http.post(`/queues/${id}/messages/visibility`, {
    receipt_handle: receiptHandle,
    visibility_timeout_seconds: visibilityTimeoutSeconds,
  });
}

export async function sendMessageBatch(
  id: string,
  messages: SendMessageRequest[],
): Promise<BatchSendResponse> {
  const { data } = await http.post<BatchSendResponse>(
    `/queues/${id}/messages/batch`,
    { messages },
  );
  return data;
}

export async function ackMessageBatch(
  id: string,
  receiptHandles: string[],
): Promise<BatchAckResponse> {
  const { data } = await http.post<BatchAckResponse>(
    `/queues/${id}/messages/ack/batch`,
    { receipt_handles: receiptHandles },
  );
  return data;
}

export async function listQueueGrants(id: string): Promise<GrantResource[]> {
  const { data } = await http.get<GrantResource[]>(`/queues/${id}/grants`);
  return data;
}

export async function createQueueGrant(
  id: string,
  granteeProjectId: string,
  actions: string[],
): Promise<GrantResource> {
  const { data } = await http.post<GrantResource>(`/queues/${id}/grants`, {
    grantee_project_id: granteeProjectId,
    actions,
  });
  return data;
}

export async function deleteQueueGrant(
  id: string,
  grantId: string,
): Promise<void> {
  await http.delete(`/queues/${id}/grants/${grantId}`);
}
